import { ReactNode, useState } from "react";
import { AlertCircle, Check, Plus, UserCog, Users, X } from "lucide-react";

import { buttonVariants } from "@/components/ui/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { cn } from "@/lib/utils";

import { cargoLabel, cargoOptions } from "../aprovacao-config-options";

interface CargosFieldProps {
  value: string[];
  onChange: (value: string[]) => void;
  error?: string;
  /** Todas as opções selecionáveis. Default: todos os cargos do sistema. */
  options?: { value: string; label: string }[];
  /** Rótulo humano de um valor selecionado. Default: rótulo do cargo. */
  labelResolver?: (value: string) => string;
  title?: string;
  icon?: ReactNode;
  itemIcon?: ReactNode;
  searchPlaceholder?: string;
  emptyTitle?: string;
  emptyDescription?: string;
}

/**
 * Seleção múltipla por cards com busca — nasceu para os cargos aprovadores de
 * uma etapa (padrão VinculosSection da AP): um botão "Adicionar" abre um
 * buscador ancorado e os itens escolhidos viram **cards** com remoção, nada de
 * dropdown gigante cobrindo o formulário. Generalizado por props (título,
 * opções, ícones) para servir outros usos de "escolher vários cargos/áreas"
 * (ex. segmentação de comunicados) sem duplicar o componente.
 */
export function CargosField({
  value,
  onChange,
  error,
  options = cargoOptions,
  labelResolver = cargoLabel,
  title = "Cargos aprovadores",
  icon = <UserCog className="size-3.5" />,
  itemIcon = <Users className="size-4" />,
  searchPlaceholder = "Buscar cargo...",
  emptyTitle = "Nenhum cargo ainda",
  emptyDescription = "Adicione quem, por cargo, aprova esta etapa.",
}: CargosFieldProps) {
  const [open, setOpen] = useState(false);

  const toggle = (val: string) =>
    onChange(value.includes(val) ? value.filter((v) => v !== val) : [...value, val]);

  const remove = (val: string) => onChange(value.filter((v) => v !== val));

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2 text-xs font-medium uppercase tracking-wider text-muted-foreground">
          <span className="grid size-6 place-items-center rounded-md bg-primary/10 text-primary">
            {icon}
          </span>
          {title}
          <span
            className={cn(
              "grid min-w-[1.25rem] place-items-center rounded-full px-1.5 py-0.5 text-[10px] font-bold tabular-nums transition-colors",
              value.length ? "bg-primary/10 text-primary" : "bg-muted text-muted-foreground",
            )}
          >
            {value.length}
          </span>
        </div>

        <div className="flex items-center gap-1">
          {value.length > 0 && (
            <button
              type="button"
              onClick={() => onChange([])}
              className="rounded-md px-2 py-1 text-xs font-medium text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
            >
              Limpar
            </button>
          )}

          <Popover open={open} onOpenChange={setOpen}>
            <PopoverTrigger
              className={buttonVariants({ variant: "outline", size: "sm", className: "gap-1.5" })}
            >
              <Plus className="size-3.5" /> Adicionar
            </PopoverTrigger>
            <PopoverContent align="end" className="w-[min(320px,85vw)] overflow-hidden p-0">
              <Command>
                <CommandInput placeholder={searchPlaceholder} />
                <CommandList>
                  <CommandEmpty>Nenhum resultado encontrado.</CommandEmpty>
                  <CommandGroup>
                    {options.map((option) => {
                      const selecionado = value.includes(option.value);
                      return (
                        <CommandItem
                          key={option.value}
                          value={option.label}
                          onSelect={() => toggle(option.value)}
                          className={cn("cursor-pointer gap-2", selecionado && "bg-primary/[0.06]")}
                        >
                          <div
                            className={cn(
                              "flex size-4 items-center justify-center rounded border transition-colors",
                              selecionado
                                ? "border-primary bg-primary text-primary-foreground"
                                : "border-input",
                            )}
                          >
                            {selecionado && <Check className="size-3" />}
                          </div>
                          <span
                            className={cn("truncate", selecionado && "font-medium text-foreground")}
                          >
                            {option.label}
                          </span>
                        </CommandItem>
                      );
                    })}
                  </CommandGroup>
                </CommandList>
              </Command>
              <div className="flex items-center justify-between border-t border-border bg-muted/30 px-3 py-2 text-[11px] text-muted-foreground">
                <span>
                  {value.length} de {options.length} selecionados
                </span>
                <button
                  type="button"
                  onClick={() => setOpen(false)}
                  className="font-medium text-primary transition-opacity hover:opacity-80"
                >
                  Concluir
                </button>
              </div>
            </PopoverContent>
          </Popover>
        </div>
      </div>

      {value.length > 0 ? (
        <div className="grid grid-cols-1 gap-2.5 sm:grid-cols-2">
          {value.map((cargo) => (
            <div
              key={cargo}
              className="group flex items-center gap-3 rounded-xl border border-border bg-card p-3 shadow-sm transition-all hover:border-primary/30 hover:shadow-md"
            >
              <span className="grid size-9 shrink-0 place-items-center rounded-lg bg-primary/10 text-primary transition-colors group-hover:bg-primary/15">
                {itemIcon}
              </span>
              <span className="min-w-0 flex-1 truncate text-sm font-medium text-foreground">
                {labelResolver(cargo)}
              </span>
              <button
                type="button"
                aria-label={`Remover ${labelResolver(cargo)}`}
                onClick={() => remove(cargo)}
                className="grid size-7 shrink-0 place-items-center rounded-md text-muted-foreground opacity-60 transition-all hover:bg-destructive/10 hover:text-destructive focus-visible:opacity-100 group-hover:opacity-100"
              >
                <X className="size-4" />
              </button>
            </div>
          ))}
        </div>
      ) : (
        <button
          type="button"
          onClick={() => setOpen(true)}
          className={cn(
            "flex w-full flex-col items-center gap-1.5 rounded-xl border border-dashed px-4 py-7 text-center transition-colors hover:border-primary/40 hover:bg-primary/[0.03]",
            error ? "border-destructive/50" : "border-border",
          )}
        >
          <span className="grid size-10 place-items-center rounded-full bg-muted text-muted-foreground">
            {itemIcon}
          </span>
          <span className="text-sm font-medium text-foreground">{emptyTitle}</span>
          <span className="text-xs text-muted-foreground">{emptyDescription}</span>
        </button>
      )}

      {error && (
        <p className="flex items-center gap-1.5 text-sm font-medium text-destructive">
          <AlertCircle className="size-4 shrink-0" /> {error}
        </p>
      )}
    </div>
  );
}
